import NotFoundError from "~/libs/errors/NotFoundError";
import type {BaseApiFetchOptions} from "~/libs/interfaces/BaseApiFetchOptions";
import type GameDrawWinnerLocal from "~/libs/interfaces/GameDrawWinnerLocal";
import type GameDrawWinnerFilters from "~/libs/interfaces/GameDrawWinnerFilters";
import type GameDrawWinnerStrapi from "~/libs/interfaces/GameDrawWinnerStrapi";
import gameDrawWinnerLocalFactory from "~/libs/factories/GameDrawWinnerLocalFactory";
import type GameUserLocal from "~/libs/interfaces/GameUserLocal";

const gameDrawWinnerStore = (type: string = 'user', key?: string) => defineStore(`game-draw-winners-${type}-${key}`, {
  state: () => {
    const state: {
      isLoading: Ref<boolean>
      limit: Ref<number>
      offset: Ref<number>
      winners: GameDrawWinnerLocal[]
      winner: Ref<GameDrawWinnerLocal | null>
      filters: GameDrawWinnerFilters
    } = {
      isLoading: ref(true),
      limit: ref(10),
      offset: ref(0),
      winners: reactive([]),
      winner: ref(null),
      filters: {},
    }
    return state
  },
  getters: {
    getIsLoading(state): boolean {
      return state.isLoading
    }
  },
  actions: {
    setLimit(limit: number) {
      this.limit = limit
    },
    setOffset(offset: number) {
      this.offset = offset
    },
    /**
     * Get items with applied filters and presets
     * @param offset
     * @param limit
     * @return GameDrawWinnerLocal[]
     */
    getFilteredWinners(offset: number = 0, limit: number = 10): GameDrawWinnerLocal[] {
      let matchedCount = 0

      const winners = this.winners.map((winner) => {
        let matchFilters = true

        if (matchFilters) {
          matchedCount = matchedCount + 1
        }

        if (matchFilters && matchedCount <= offset) {
          matchFilters = false
        } else if (matchFilters && matchedCount > (offset + limit)) {
          matchFilters = false
        }

        return {
          ...winner,
          isEnabled: matchFilters
        }
      })
      return winners
    },


    /**
     * Get count of getFilteredWinners
     * @return number
     */
    getFilteredWinnersCount(): number {
      const winners = this.getFilteredWinners(0, this.winners.length)
      return winners?.length || 0
    },

    /**
     * Get count of items
     * @return number
     */
    getWinnersCount(): number {
      return this.winners.length || 0
    },
    /**
     * Get winner by id
     * @param id
     */
    getWinner(id: string|number): GameDrawWinnerLocal | undefined {
      const winner = this.winners.find((winner) => {
        return String(winner.id) === String(id)
      })
      return winner
    },

    /**
     * Get last winner
     */
    getLastWinner(): GameDrawWinnerLocal | undefined {
      return this.winners.at(0)
    },
    /**
     * Fetch filter changed
     * @param filters
     */
    filterChanged(filters?: GameDrawWinnerFilters): boolean {
      const sameArrays = (a?: string[], b?: string[]) => {
        if ((!a && b) || (a && !b)) {
          return false
        }
        if (!a && !b) {
          return true
        }
        if (a === b) {
          return true
        }
        if (a && b) {
          if (a.length !== b.length) {
            return false
          }
          const sortedA = a.sort()
          const sortedB = b.sort()

          for (let i = 0; i < sortedA.length; i++) {
            if (sortedA[i] !== sortedB[i]) {
              return false
            }
          }
        }
        return true
      }

      // check item ids filter
      if (!sameArrays(filters?.ids, this.filters.ids)) {
        return true
      }

      // check item slugs filter
      if (!sameArrays(filters?.userIds, this.filters.userIds)) {
        return true
      }

      return false
    },

    checkFiltersChanges(filters: GameDrawWinnerFilters): void {
      // reset items if filter changed
      if (this.filterChanged(filters)) {
        this.filters.ids = filters.ids
        this.filters.userIds = filters.userIds
        this.winners = reactive([])
        this.winner = null
      }
    },

    /**
     * Fetch my last win
     */
    async fetchUserLastWins(): Promise<GameUserLocal | null> {
      const logger = useFrontendLogger()
      const persistedStore = usePersistedStore()
      const userStore = useUserStore()
      if (!userStore.isLoggedIn) {
        return null
      }
      const { find } = useStrapi()
      try {
        const payload = {
          pagination: {
            start: this.offset,
            limit: this.limit,
          },
          sort: ["createdAt:desc"]
        }
        const result = await find<GameDrawWinnerStrapi>('game-draw-winners/my-wins', payload)
        //@ts-ignore
        const items = result.data || []
        this.processData(items, this.limit === 1)
        persistedStore.setLastSync(this.$id)
        this.isLoading = false
      } catch (e) {
        logger?.error('useUserStore:fetchMyLastWin - exception', {
          errorDetail: e,
        })
        throw e
      }
      return null
    },

    /**
     * Fetch items from server
     * @return {Promise<void>}
     */
    async fetchWinners(
      filters: GameDrawWinnerFilters = {},
      options: BaseApiFetchOptions = {}
    ): Promise<void> {
      const logger = useFrontendLogger()
      const persistedStore = usePersistedStore()
      const { find } = useStrapi()

      this.isLoading = true

      const apiFilters = {
        ...(filters.ids && {
          documentId: {
            $in: filters.ids,
          }
        }),
        ...(filters.userIds && {
          user: {
            documentId: {
              $in: filters.userIds,
            }
          }
        })
      }

      const params = {
        filters: apiFilters,
        pagination: {
          start: this.offset,
          limit: this.limit,
        },
        sort: ["createdAt:desc"]
      }

      try {
        const result = await find<GameDrawWinnerStrapi>('game-draw-winners', params)
        //@ts-ignore
        const items = result.data || []
        this.processData(items, this.limit === 1)
        persistedStore.setLastSync(this.$id)
        this.isLoading = false
      } catch (e) {
        logger?.error('useGameDrawWinnersStore:fetchWinners - exception', {
          errorDetail: e,
          strapiCallParams: params,
        })
        this.isLoading = false
        throw e
      }
    },
    /**
     * Process loaded data from Strapi
     * @param newItems
     * @param isDetail
     */
    processData(newItems: GameDrawWinnerStrapi[], isDetail: boolean = false):void {
      if (newItems.length) {
        // check item detail load
        if (isDetail && newItems[0]) {
          this.setItem(gameDrawWinnerLocalFactory.createGameDrawFromStrapi(newItems[0]))
        } else if (isDetail && (!newItems || !newItems.length)) {
          throw new NotFoundError('Winner not found')
        }
        if (newItems && newItems.length) {
          // add new items to list
          this.addItems(newItems.map(gameDrawWinnerLocalFactory.createGameDrawFromStrapi).filter(item => !!item), true)
        }
      } else if (isDetail) {
        throw new NotFoundError('Winner not found')
      }
    },
    /**
     * Add items to store - remove duplicities
     * @param newItems
     * @param rewrite
     */
    addItems(newItems?: GameDrawWinnerLocal[], rewrite: boolean = false): void {
      newItems?.forEach((newItem) => {
        const foundIndex = this.winners.findIndex(winner => winner.id === newItem.id)
        if (foundIndex >= 0 && rewrite) {
          this.winners.splice(foundIndex, 1)
        }
        if (foundIndex === -1 || rewrite) {
          this.winners.push(newItem)
        }
      })
    },

    setItem(item?: GameDrawWinnerLocal): void {
      if (item) {
        this.winner = item
      } else {
        this.winner = null
      }
    }

  }
})

export const useGameDrawWinnersStore = (type?: string, key?: string) => {
  return gameDrawWinnerStore(type, key)()
}
